(function () {
  const API_ENDPOINT = '/api/warranty/register';
  const STORAGE_KEY = 'mofcoverWarrantyRegistrations';
  const REQUIRED_FIELDS = {
    firstName: 'Please enter your first name.',
    lastName: 'Please enter your last name.',
    email: 'Please enter your email address.',
    orderNumber: 'Please enter your order number.',
    purchaseChannel: 'Please select where you purchased your product.',
    productType: 'Please select your product type.',
    purchaseDate: 'Please select your purchase date.'
  };

  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  function wait(ms) {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
  }

  async function mockSubmitWarrantyRegistration(formData) {
    await wait(800 + Math.floor(Math.random() * 401));

    const registration = createWarrantyRecord(formData);
    saveWarrantyRecord(registration);

    return {
      ok: true,
      endpoint: API_ENDPOINT,
      registration,
      submittedAt: new Date().toISOString(),
      orderNumber: formData.get('orderNumber')
    };
  }

  function resolveEndpoint(apiBase) {
    const base = String(apiBase || '').trim();

    if (!base) {
      return '';
    }

    return `${base.replace(/\/$/, '')}/register`;
  }

  async function submitWarrantyRegistration(formData, apiBase) {
    const endpoint = resolveEndpoint(apiBase);

    if (!endpoint) {
      return mockSubmitWarrantyRegistration(formData);
    }

    const response = await fetch(endpoint, {
      method: 'POST',
      body: formData
    });

    const result = await response.json().catch(() => ({}));

    if (!response.ok || result.ok === false) {
      throw new Error(result.message || 'Warranty registration failed.');
    }

    return result;
  }

  function createWarrantyRecord(formData) {
    const proof = formData.get('proofOfPurchase');
    const submittedAt = new Date().toISOString();

    return {
      warrantyId: `MOF-WTY-${Date.now().toString(36).toUpperCase()}`,
      status: 'Registration Received',
      warrantyTerm: '1-Year Limited Warranty',
      firstName: String(formData.get('firstName') || '').trim(),
      lastName: String(formData.get('lastName') || '').trim(),
      email: String(formData.get('email') || '').trim().toLowerCase(),
      orderNumber: String(formData.get('orderNumber') || '').trim(),
      purchaseChannel: String(formData.get('purchaseChannel') || '').trim(),
      productType: String(formData.get('productType') || '').trim(),
      productSize: String(formData.get('productSize') || '').trim(),
      purchaseDate: String(formData.get('purchaseDate') || '').trim(),
      proofOfPurchaseName: proof && proof.name ? proof.name : '',
      message: String(formData.get('message') || '').trim(),
      submittedAt
    };
  }

  function readWarrantyRecords() {
    try {
      return JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
    } catch (error) {
      return [];
    }
  }

  function saveWarrantyRecord(registration) {
    try {
      const records = readWarrantyRecords().filter((record) => {
        return !(record.email === registration.email && record.orderNumber.toLowerCase() === registration.orderNumber.toLowerCase());
      });

      records.unshift(registration);
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(records.slice(0, 20)));
    } catch (error) {
      // Local storage is only used for the demo flow. Production data should be saved server-side.
    }
  }

  function getField(form, fieldName) {
    return form.elements[fieldName];
  }

  function setFieldError(form, fieldName, message) {
    const field = getField(form, fieldName);
    const error = form.querySelector(`[data-error-for="${fieldName}"]`);

    if (field) {
      field.setAttribute('aria-invalid', message ? 'true' : 'false');
    }

    if (error) {
      error.textContent = message;
      if (field && message) {
        if (!error.id) {
          error.id = `${field.id || fieldName}-error`;
        }
        field.setAttribute('aria-describedby', error.id);
      } else if (field) {
        field.removeAttribute('aria-describedby');
      }
    }
  }

  function validateForm(form) {
    let isValid = true;

    Object.keys(REQUIRED_FIELDS).forEach((fieldName) => {
      const field = getField(form, fieldName);
      const value = field ? String(field.value).trim() : '';
      const message = value ? '' : REQUIRED_FIELDS[fieldName];

      setFieldError(form, fieldName, message);

      if (message) {
        isValid = false;
      }
    });

    const emailField = getField(form, 'email');
    const email = emailField ? String(emailField.value).trim() : '';

    if (email && !EMAIL_PATTERN.test(email)) {
      setFieldError(form, 'email', 'Please enter a valid email address, such as name@example.com.');
      isValid = false;
    }

    return isValid;
  }

  function setSubmitting(form, isSubmitting) {
    const submitButton = form.querySelector('[data-warranty-submit]');
    const label = form.querySelector('[data-submit-label]');
    const loadingLabel = form.querySelector('[data-submit-loading]');

    if (!submitButton) {
      return;
    }

    submitButton.disabled = isSubmitting;
    submitButton.setAttribute('aria-busy', isSubmitting ? 'true' : 'false');

    if (label && loadingLabel) {
      label.hidden = isSubmitting;
      loadingLabel.hidden = !isSubmitting;
    }
  }

  function showFormError(form, message) {
    const banner = form.querySelector('[data-warranty-form-error]');

    if (!banner) {
      return;
    }

    banner.textContent = message || '';
    banner.hidden = !message;
  }

  function showSuccess(form) {
    const shell = form.closest('.mofcover-warranty__form-shell');
    const successCard = shell ? shell.querySelector('[data-warranty-success]') : null;

    form.hidden = true;

    if (successCard) {
      successCard.hidden = false;
      successCard.focus({ preventScroll: true });
      successCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  function initializeWarrantyForm(form) {
    const fileInput = form.querySelector('[data-warranty-file]');
    const fileName = form.querySelector('[data-warranty-file-name]');

    if (fileInput && fileName) {
      fileInput.addEventListener('change', () => {
        fileName.textContent = fileInput.files && fileInput.files.length ? fileInput.files[0].name : 'No file selected';
      });
    }

    const clearFieldError = (event) => {
      const target = event.target;

      if (target && target.name && REQUIRED_FIELDS[target.name]) {
        setFieldError(form, target.name, '');
        showFormError(form, '');
      }
    };

    form.addEventListener('input', clearFieldError);
    form.addEventListener('change', clearFieldError);

    form.addEventListener('submit', async (event) => {
      event.preventDefault();

      if (form.dataset.submitting === 'true') {
        return;
      }

      showFormError(form, '');

      if (!validateForm(form)) {
        showFormError(form, 'Please review the highlighted fields before submitting your warranty registration.');
        const firstInvalid = form.querySelector('[aria-invalid="true"]');
        if (firstInvalid) {
          firstInvalid.focus();
        }
        return;
      }

      form.dataset.submitting = 'true';
      setSubmitting(form, true);

      try {
        await submitWarrantyRegistration(new FormData(form), form.dataset.apiBase || '');
        showSuccess(form);
      } catch (error) {
        showFormError(form, 'We could not submit your warranty registration right now. Please try again.');
        setSubmitting(form, false);
        form.dataset.submitting = 'false';
      }
    });
  }

  window.MOFCOVERWarranty = window.MOFCOVERWarranty || {};
  window.MOFCOVERWarranty.storageKey = STORAGE_KEY;
  window.MOFCOVERWarranty.submitRegistration = submitWarrantyRegistration;
  window.MOFCOVERWarranty.mockSubmitRegistration = mockSubmitWarrantyRegistration;

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-warranty-form]').forEach(initializeWarrantyForm);
  });
})();
